import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Component,
  Injectable,
  Injector,
  OnDestroy,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import * as dateFns from 'date-fns';
import { Guid } from 'guid-typescript';
import { Subscription, firstValueFrom, lastValueFrom } from 'rxjs';
import { MethodInfo } from '../../annotations/sia-base-script.annotation';
import { CrudDialogComponent } from '../../dialogs/crud-dialog/crud-dialog.component';
import { SiaChoiceDialogComponent } from '../../dialogs/sia-choice-dialog/sia-choice-dialog';
import { YesNoDialogComponent } from '../../dialogs/yes-no-dialog/yes-no-dialog';
import { YesNoFeedbackDialogComponent } from '../../dialogs/yes-no-feedback-dialog/yes-no-feedback-dialog';
import { GoogleCalendarEvent } from '../../interfaces/google-calendar-event';
import { SiaArchivePath } from '../../interfaces/sia-file-path';
import { SiaRouteData } from '../../interfaces/sia-navigation';
import { SiaSessionParam } from '../../interfaces/sia-session-param';
import { SiaTokenUser } from '../../interfaces/sia-token-user';
import { SiaBaseActionService } from '../../services/base-action.service';
import { SiaComponentConfigService } from '../../services/component-config.service';
import { FileService } from '../../services/file.service';
import { generateGoogleCalendarLink } from '../../services/functions-utils.service';
import { SiaHttpService } from '../../services/http.service';
import { SiaI18nService } from '../../services/i18n.service';
import { SiaLayoutService } from '../../services/layout.service';
import {
  SiaNewRouterService,
  retriveCurrentRouteData,
} from '../../services/new-router.service';
import { notify } from '../../services/notify';
import { SiaParamsAndPatternsService } from '../../services/params-and-patterns.service';
import { SiaRadarService } from '../../services/radar.service';
import { SiaReportPrintService } from '../../services/report-print.service';
import { SiaSessionService } from '../../services/session.service';
import { SiaSignalrService } from '../../services/signalr.service';
import { SiaSizeDetectorService } from '../../services/size-detector.service';
import { SiaSqlQueryService } from '../../services/sqlquery.service';
import { ThemeService } from '../../services/theme.service';
import { SiaUsersService } from '../../services/users.service';

@Injectable({ providedIn: 'root' })
@Component({
  selector: 'sia-base-script',
  template: '',
  styles: [''],
  standalone: true,
})
export class SiaBaseScriptComponent implements OnDestroy {
  protected siaParamsAndPatternsService = inject(SiaParamsAndPatternsService);
  protected componentConfigService = inject(SiaComponentConfigService);
  protected siaReportPrintService = inject(SiaReportPrintService);
  protected siaBaseActionService = inject(SiaBaseActionService);
  protected siaSizeDetector = inject(SiaSizeDetectorService);
  protected siaSqlQueryService = inject(SiaSqlQueryService);
  protected siaRouterService = inject(SiaNewRouterService);
  protected siaSessionService = inject(SiaSessionService);
  protected siaSignalrService = inject(SiaSignalrService);
  protected siaRadarService = inject(SiaRadarService);
  protected siaUsersService = inject(SiaUsersService);
  protected siaHttpService = inject(SiaHttpService);
  protected siaThemeService = inject(ThemeService);
  protected i18nService = inject(SiaI18nService);
  protected fileService = inject(FileService);
  protected httpClient = inject(HttpClient);
  protected dialog = inject(MatDialog);
  protected layoutService = inject(SiaLayoutService);
  private baseInjector = inject(Injector);

  #audio = new Audio();
  protected _radarOpen: boolean = false;
  protected _radarSelection?: Promise<any>;
  protected baseRouterPageData: SiaRouteData = {};
  promiseResolver: any;
  private _superSubs: Subscription[] = [];
  dateFns = dateFns;
  yesNoDialog = YesNoDialogComponent;
  crudDialog = CrudDialogComponent;
  yesNoFeedbackDialog = YesNoFeedbackDialogComponent;
  /**
   * Espressione regolare che puù essere utilizzata dagli script per validare una email
   */
  emailReg: RegExp = /^$|^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/i;

  /**
   * Espressione regolare che puù essere utilizzata dagli script per validare un sito web
   */
  websiteReg: RegExp =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;
  /**
   * Espressione regolare che può essere utilizzata dagli script per validare un numero di telefono
   */
  phoneReg: RegExp =
    /^(?:(?:\(?(?:00|\+)([1-4]\d\d|[1-9]\d+)\)?)[\-\.\ \\\/]?)?((?:\(?\d{1,}\)?[\-\.\ \\\/]?)+)(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i;

  /**
   * Oggetto contenete le informazioni dell'utente
   */
  user: SiaTokenUser | null;
  /**
   * Oggetto contenete i default dell'utente
   */
  userDefaults: any = {};
  AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;

  get isDark() {
    return this.siaThemeService.getCurrentTheme() == 'dark';
  }
  ngOnInit() {
    var pageData = null;
    runInInjectionContext(this.baseInjector, () => {
      pageData = retriveCurrentRouteData() ?? {};
    });
    this.baseRouterPageData = pageData!;
  }

  ngOnDestroy() {
    this.promiseResolver = null;
    this._radarSelection = undefined;
    this._superSubs.forEach((sub) => {
      sub.unsubscribe();
    });
    this._superSubs = [];
  }

  createHttpParams(params: { [key: string]: any }) {
    let httpParams = new HttpParams();
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const value = params[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value);
        }
      }
    }
    return httpParams;
  }

  constructor() {
    var promiseResolve: any, promiseReject;
    this._radarSelection = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });
    this.promiseResolver = promiseResolve;

    let s0 = this.siaRadarService
      .finalSelectionChanged$()
      .subscribe((selection: any) => {
        if (this._radarOpen) {
          this.promiseResolver(selection);
          this._radarOpen = false;
        }
      });
    this._superSubs.push(s0);
    this.user = this.siaUsersService.getUserData();
    var defaults = this.siaUsersService.getUserDefaultsData();
    if (defaults) {
      defaults.forEach((defaultValue) => {
        this.userDefaults[defaultValue.id?.toLowerCase() ?? ''] =
          defaultValue.value;
      });
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'closeOutlet(outlet: string)',
    comment: 'Chiude un outlet specifico',
    async: false,
    return: 'void',
  })
  closeOutlet(outlet: string) {
    if (!outlet) {
      this.navigateBack();
      return;
    }
    this.siaRouterService.closeOutlet(outlet);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'yesNoDialogOpen(title: string, negativeMeaning?: boolean, description?: string,disableKeyboardShortcut?: boolean, hideNo?: boolean, dialogTitle?: string, yesLabel?: string, noLabel?: string): Promise<any>',
    comment: 'Apre un dialogo di conferma',
    async: true,
    return: 'Promise<any>',
  })
  async yesNoDialogOpen(
    message: string,
    negativeMeaning: boolean = false,
    description: string = '',
    disableKeyboardShortcut: boolean = false,
    hideNo: boolean = false,
    dialogTitle?: string,
    yesLabel?: string,
    noLabel?: string,
  ) {
    var dialogRef = this.dialog.open(this.yesNoDialog, {
      width: '350px',
      data: {
        title: message,
        negativeMeaning: negativeMeaning,
        description: description,
        disableKeyboardShortcut: disableKeyboardShortcut,
        hideNo: hideNo,
        dialogTitle: dialogTitle,
        yesLabel: yesLabel,
        noLabel: noLabel,
      },
    });
    return await firstValueFrom(dialogRef.afterClosed());
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'crudDialogOpen(message: string, email: string = "", disableKeyboardShortcut: boolean = false): Promise<any>',
    comment: 'Apre un dialogo di conferma con email',
    async: true,
    return: 'Promise<any>',
  })
  async crudDialogOpen(
    message: string,
    email: string = '',
    disableKeyboardShortcut: boolean = false,
  ) {
    var dialogRef = this.dialog.open(CrudDialogComponent, {
      width: '350px',
      data: {
        title: message,
        value: email,
        label: 'Email',
        errorMessage: 'Inserisci un indirizzo email valido',
        yesLabel: 'Conferma',
        noLabel: 'Chiudi',
        disableKeyboardShortcut: disableKeyboardShortcut,
        regexPattern: '/^[^@]+@[^@]+\.[^@]+$/',
      },
    });
    return await firstValueFrom(dialogRef.afterClosed());
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'openCrudDialog(message: string, value?: string, disableKeyboardShortcut?: boolean, label?: string, errorMessage?: string, yesLabel?: string, noLabel?: string, regexPattern?: string): Promise<any>',
    comment: 'Apre un dialogo di conferma con email',
    async: true,
    return: 'Promise<any>',
  })
  async openCrudDialog(
    message: string,
    value: string = '',
    disableKeyboardShortcut: boolean = false,
    label: string = '',
    errorMessage: string = '',
    yesLabel: string = '',
    noLabel: string = '',
    regexPattern: string = '',
  ) {
    var dialogRef = this.dialog.open(CrudDialogComponent, {
      width: '350px',
      data: {
        title: message,
        value: value,
        label: label,
        errorMessage: errorMessage,
        yesLabel: yesLabel,
        noLabel: noLabel,
        disableKeyboardShortcut: disableKeyboardShortcut,
        regexPattern: regexPattern,
      },
    });
    return await firstValueFrom(dialogRef.afterClosed());
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'yesNoFeedbackDialogOpen(message: string, description: string,negativeMeaning: boolean, style: string): Promise<any>',
    comment: "Apre un dialogo di conferma con feedback dell'utente",
    async: true,
    return: 'Promise<any>',
  })
  async yesNoFeedbackDialogOpen(
    message: string,
    negativeMeaning: boolean = false,
    description: string = '',
    style: string = '',
  ) {
    var dialogRef = this.dialog.open(this.yesNoFeedbackDialog, {
      width: '350px',
      data: {
        title: message,
        description: description,
        negativeMeaning: negativeMeaning,
        style: style,
      },
    });
    return await firstValueFrom(dialogRef.afterClosed());
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'choicesDialogOpen(options: any): Promise<any>',
    comment: 'Apre un dialogo di scelta',
    async: true,
    return: 'Promise<any>',
  })
  async choicesDialogOpen(options: any, params?: any): Promise<any> {
    var dialogRef = this.dialog.open(SiaChoiceDialogComponent, {
      width: 'min(95% , 600px)',
      data: options,
      disableClose: true,
    });
    var script = await firstValueFrom(dialogRef.afterClosed());
    return await this.execScript(script, params);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'currentComponentId',
    comment: "Recupera l'id del componente corrente",
    async: false,
    return: 'string',
  })
  get currentComponentId(): string {
    return this.baseRouterPageData.componentId ?? '';
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'currentGuid',
    comment: "Recupera l'id del guid corrente",
    async: false,
    return: 'string',
  })
  get currentGuid(): string {
    return this.baseRouterPageData.guid ?? '';
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'execSP(spName: string, parameters: any = null): Promise<any>',
    comment:
      'Utilizzato dagli script per interrogare il DB tramite le stored procedure(per motivi di sicurezza)',
    async: true,
    return: 'Promise<any>',
  })
  async execSP(spName: string, parameters: any = {}): Promise<any> {
    return await this.siaSqlQueryService.execSP(spName, parameters);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'copyToClipBoard(textToCopy:string): Promise<any>',
    comment:
      'Copia un testo negli appunti. Utilizza navigator.clipboard.writeText',
    async: true,
    return: 'Promise<any>',
  })
  async copyToClipBoard(textToCopy: string): Promise<any> {
    return await navigator.clipboard.writeText(textToCopy);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'translate(toTranslate:string)',
    comment: 'Traduce una stringa utilizzando il servizio di i18n',
    async: false,
    return: 'string',
  })
  translate(toTranslate: string) {
    return this.i18nService.translate(toTranslate);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'getLanguage()',
    comment: 'Restituisce la lingua corrente utilizzando il servizio di i18n',
    async: false,
    return: 'string',
  })
  getLanguage() {
    return this.i18nService.language;
  }
  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'executeQuery(query: string, parameters: any = null): Promise<any>',
    comment: 'Utilizzato dagli script per interrogare il DB tramite query',
    async: true,
    return: 'Promise<any>',
  })
  async executeQuery(query: string, parameters: any = undefined): Promise<any> {
    return await this.siaSqlQueryService.executeQuery(query, parameters);
  }
  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'checkSessionValue(paramName: string, value: any, guid?: string)',
    comment: 'Verifica il valore di un parametro di sessione',
    async: false,
    return: 'boolean',
  })
  checkSessionValue(paramName: string, value: any, guid?: string) {
    guid = guid ?? this.baseRouterPageData.guid;
    let sessionValue = this.siaSessionService.getSessionParamValue(
      paramName,
      guid,
    );
    if (typeof value == 'string' && typeof sessionValue == 'string') {
      return value.toLowerCase() == sessionValue.toLowerCase();
    }
    return sessionValue == value;
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'getSessionValue(paramName: string, guid?: string)',
    comment: 'Ritorna il valore di un parametro di sessione',
    async: false,
    return: 'any',
  })
  getSessionValue(paramName: string, guid?: string) {
    guid = !guid ? this.baseRouterPageData.guid : guid;
    let sessionValue = this.siaSessionService.getSessionParamValue(
      paramName,
      guid,
    );
    return sessionValue;
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'getLocalStorageValue(paramName: string)',
    comment: 'Ritorna il valore di un parametro di local storage',
    async: false,
    return: 'any',
  })
  getLocalStorageValue(paramName: string) {
    let localStorageValue = localStorage.getItem(paramName);
    return localStorageValue;
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'getDepartment()',
    comment: "Recupera il dipartimento dell'utente corrente",
    async: false,
    return: 'string',
  })
  getDepartment() {
    return this.getLocalStorageValue('department') || '';
  }

  getIsMobile() {
    return this.siaSizeDetector.currentSize.isMobile;
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'downloadFile(path: string, name?: string, isRelativePath?: boolean, isSystemArchive?: boolean)',
    comment: 'Scarica un file',
    async: true,
    return: 'void',
  })
  downloadFile(
    path: string,
    name?: string,
    isRelativePath?: boolean,
    isSystemArchive?: boolean,
  ) {
    try {
      const fileName = name ?? path.split(/[\/\\]+/).pop() ?? 'download';
      const fileNameWithExtension = fileName.includes('.')
        ? fileName
        : fileName + (path.match(/\.[^.]*$/) ? path.match(/\.[^.]*$/)![0] : '');

      this.fileService.downloadFile(
        path,
        fileNameWithExtension,
        isRelativePath ?? false,
        isSystemArchive ?? false,
      );
    } catch (error) {
      console.error('Errore nel download del file:', error);
      notify('Errore nel download del file.', 2000, 'error');
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'updateParametersAndNotify(params: SiaSessionParam[], guid?: string, type: "all" | "tabs" = "all")',
    comment: 'Aggiorna i parametri di sessione e notifica il cambiamento',
    async: false,
    return: 'void',
  })
  updateParametersAndNotify(
    params: SiaSessionParam[],
    guid?: string,
    type: 'all' | 'tabs' = 'all',
  ) {
    guid = guid ?? this.baseRouterPageData.guid;
    this.siaSessionService.updateSessionParams(guid, params);
    if (type == 'all') {
      this.siaParamsAndPatternsService.notifyHardParamsUpdated(guid);
    } else {
      this.siaParamsAndPatternsService.notifyParamsUpdatedWithGuidTabs(guid);
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'callApi(method: "get" | "post" | "put" | "delete", url: string, data: any = null)',
    comment: 'Effettua una chiamata ad un API',
    async: true,
    return: 'Promise<any>',
  })
  async callApi(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data: any = null,
  ) {
    switch (method) {
      case 'get':
        return await lastValueFrom(this.httpClient.get(url));
      case 'post':
        return await lastValueFrom(this.httpClient.post(url, data));
      case 'put':
        return await lastValueFrom(this.httpClient.put(url, data));
      case 'delete':
        return await lastValueFrom(this.httpClient.delete(url));
      default:
        return;
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'callSiaApi(method: "get" | "post" | "put" | "delete", endpoint: string, data?: any, options?: {}, geoArea?: string)',
    comment: 'Effettua una chiamata ad un API SIA',
    async: true,
    return: 'Promise<any>',
  })
  async callSiaApi(
    method: 'get' | 'post' | 'put' | 'delete',
    endpoint: string,
    data?: any,
    options?: {},
    geoArea?: string,
  ) {
    switch (method) {
      case 'get':
        return await this.siaHttpService.get(endpoint, options, geoArea);
      case 'post':
        return await this.siaHttpService.post(endpoint, data, options, geoArea);
      case 'put':
        return await this.siaHttpService.put(endpoint, data, options, geoArea);
      case 'delete':
        return await this.siaHttpService.delete(endpoint, options, geoArea);
      default:
        return;
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'notify(message: string, durationMs: number, type: "error" | "success" | "warning")',
    comment: 'Mostra una notifica',
    async: false,
    return: 'void',
  })
  notify(
    message: string,
    durationMs: number,
    type: 'error' | 'success' | 'warning' | 'info',
    icon?: string,
    animation?: string,
  ) {
    notify(message, durationMs, type, icon, animation);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'validateEmail(email: string): boolean',
    comment: 'Valida un indirizzo email',
    async: false,
    return: 'boolean',
  })
  validateEmail(email: string): boolean {
    let a = email.trim();
    return this.emailReg.test(a);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'validateWebsite(website: string): boolean',
    comment: 'Valida un sito web',
    async: false,
    return: 'boolean',
  })
  validateWebsite(website: string): boolean {
    return this.websiteReg.test(website);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'validatePhone(phone: string): boolean',
    comment: 'Valida un numero di telefono',
    async: false,
    return: 'boolean',
  })
  validatePhone(phone: string): boolean {
    return this.phoneReg.test(phone);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'extractPhone(phone: string): string | null',
    comment: 'Estrae il numero di telefono da una stringa',
    async: false,
    return: 'string | null',
  })
  extractPhone(phone: string): string | null {
    if (!phone) {
      return '';
    }
    var extractedPhoneNumber = phone.replace(/[^0-9]/g, '');
    if (extractedPhoneNumber && this.validatePhone(extractedPhoneNumber)) {
      return extractedPhoneNumber;
    }
    return null;
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'validateCustomRegex(regex: RegExp, str: string): boolean',
    comment: 'Valida una stringa tramite una regex personalizzata',
    async: false,
    return: 'boolean',
  })
  validateCustomRegex(regex: RegExp, str: string): boolean {
    return regex.test(str);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'execScript(script: string, params?: any)',
    comment: 'Esegue uno script',
    async: true,
    return: 'Promise<any>',
  })
  async execScript(script: string, params?: any) {
    let injectedFunction: any;
    try {
      injectedFunction = new this.AsyncFunction('params', script);
      // Call the newly created function with the provided parameter
      const result = await injectedFunction.call(this, params);
      if (result && result.then && typeof result.then === 'function') {
        return await result;
      }
      return result;
    } catch (error) {
      console.error('Errore eseguendo script: ', error, script);
    } finally {
      // Remove the reference to the function
      injectedFunction = null;
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'navigateMenu(menuId: string, params: SiaSessionParam[], mode: string = "read", subTitle: string = "", subTitleAdditionalInformation: string = "", additionalTitle: string = "", routeParams: string[] = [])',
    comment: 'Naviga ad un menu del portale',
    async: false,
    return: 'void',
  })
  navigateMenu(
    menuId: string,
    params: SiaSessionParam[],
    mode: string = 'read',
    subTitle: string = '',
    subTitleAdditionalInformation: string = '',
    additionalTitle: string = '',
    routeParams: string[] = [],
  ) {
    var guid = Guid.create().toString();
    this.siaRouterService.navigateMenuWithParams(
      menuId,
      params,
      mode,
      guid,
      subTitle,
      subTitleAdditionalInformation,
      this.baseRouterPageData.outlet,
      routeParams,
    );
    this.layoutService.showAdditionalTitle(additionalTitle, guid);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'navigateBack()',
    comment: 'Torna indietro',
    async: false,
    return: 'void',
  })
  navigateBack() {
    if (!this.baseRouterPageData.outlet) {
      return Promise.resolve();
    }

    let outletToClose = this.baseRouterPageData.outlet.replace('tabs', '');
    if (outletToClose == 'base') {
      return this.siaRouterService.navigateBack();
    } else {
      return this.siaRouterService.navigateBackSecondaryOutlet(outletToClose);
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      "navigateMenuPopup(menuId: string, params: SiaSessionParam[], mode: string = 'read', subTitle: string = '', titleAdditionalInformation: string = '')",
    comment: 'Naviga ad un menu popup',
    async: false,
    return: 'void',
  })
  navigateMenuPopup(
    menuId: string,
    params: SiaSessionParam[],
    mode: string = 'read',
    subTitle: string = '',
    titleAdditionalInformation: string = '',
  ) {
    var guid = Guid.create().toString();
    this.siaRouterService.navigateMenuWithParams(
      'popup^' + menuId,
      params,
      mode,
      guid,
      subTitle,
      titleAdditionalInformation,
      this.baseRouterPageData.outlet,
    );
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'openWindow(url: string)',
    comment: 'Apre una finestra',
    async: false,
    return: 'void',
  })
  openWindow(url: string) {
    window.open(url, '_blank');
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'navigateRadar(menuId: string, menuMobileId?: string, params: SiaSessionParam[] = [], modality: "single" | "multiple" = "single", foreignKey?: any): Promise<any>',
    comment: 'Naviga ad un radar',
    async: true,
    return: 'Promise<any>',
  })
  navigateRadar(
    menuId: string,
    menuMobileId?: string,
    params: SiaSessionParam[] = [],
    modality: 'single' | 'multiple' = 'single',
    foreignKey?: any,
  ): Promise<any> {
    this._radarOpen = true;
    params.push({ id: 'selectionMode', value: modality });
    if (foreignKey) {
      params.push({ id: '__foreignkey', value: foreignKey });
    }
    if (menuMobileId && this.siaSizeDetector.currentSize.isMobile) {
      this.siaRouterService.navigateMenuWithParams(
        'radar^' + menuMobileId,
        params,
        'read',
        Guid.create().toString(),
        '',
        '',
        this.baseRouterPageData.outlet,
      );
    } else {
      this.siaRouterService.navigateMenuWithParams('radar^' + menuId, params);
    }

    var promiseResolve: any, promiseReject;
    this._radarSelection = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });
    this.promiseResolver = promiseResolve;

    return this._radarSelection;
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'userHaveGroup(groupId: number | string)',
    comment: "Verifica se l'utente appartiene ad un gruppo",
    async: false,
    return: 'boolean',
  })
  userHaveGroup(groupId: number | string) {
    return this.siaUsersService.userHaveGroup(groupId);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'openEmail(email: string)',
    comment: 'Apre un client di posta elettronica',
    async: false,
    return: 'void',
  })
  openEmail(email: string) {
    if (email) {
      window.open('mailto:' + email, '_blank');
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'openTelephone(phone: string)',
    comment: 'Chiama un numero di telefono',
    async: false,
    return: 'void',
  })
  openTelephone(phone: string) {
    if (phone) {
      window.open('tel:' + phone, '_blank');
    }
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'openMap(address: string)',
    comment: 'Apre Google Maps con un indirizzo',
    async: false,
    return: 'void',
  })
  openMap(address: string) {
    if (address) {
      window.open('http://maps.google.com/?q=' + address + '"');
    }
  }
  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'notifyParamsUpdated(guid: string)',
    comment: 'Notifica il cambiamento dei parametri di sessione',
    async: false,
    return: 'void',
  })
  notifyParamsUpdated(guid: string) {
    this.siaParamsAndPatternsService.notifyHardParamsUpdated(guid);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'openUrl(url: string)',
    comment: 'Apre un URL',
    async: false,
    return: 'void',
  })
  openUrl(url: string) {
    if (url) {
      if (url.substr(0, 4) != 'http') {
        window.open('//' + url);
      } else {
        window.open(url);
      }
    }
  }
  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'sendSignalrMessage(componentId: string, messageTypeId: string, message: any)',
    comment: 'Invia un messaggio tramite SignalR',
    async: false,
    return: 'void',
  })
  sendSignalrMessage(componentId: string, messageTypeId: string, message: any) {
    this.siaSignalrService.sendPortalMessage(
      componentId,
      messageTypeId,
      message,
    );
  }
  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'hideOrShowAction(actionId: string, show: boolean, componentId?: string)',
    comment: 'Mostra o nasconde un azione',
    async: false,
    return: 'void',
  })
  hideOrShowAction(
    actionId: string,
    show: boolean,
    componentId?: string,
    guid?: string,
  ) {
    if (!componentId) {
      componentId = this.baseRouterPageData.componentId ?? '';
    }
    if (!guid) {
      guid = this.baseRouterPageData.guid ?? '';
    }
    this.siaBaseActionService.handleAction(componentId, guid, actionId, show);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'printReport(reportName: string, parameters: SiaSessionParam[])',
    comment: 'Stampa un report',
    async: false,
    return: 'void',
  })
  async printReport(
    reportName: string,
    parameters: SiaSessionParam[],
  ): Promise<void> {
    await this.siaReportPrintService.downloadReport({
      reportName: reportName,
      reportParams: parameters,
      format: 'pdf',
    });
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'setPageMode(mode: string, guid: string)',
    comment: 'Imposta la modalità di visualizzazione di una pagina',
    async: false,
    return: 'void',
  })
  setPageMode(mode: string, guid?: string) {
    this.siaRouterService.notifyPageMode(guid ?? '', mode);
  }

  protected sanitizer = inject(DomSanitizer);

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'getAvatarUrl(userId?: number): Promise<SafeUrl>',
    comment: "Recupera l'url dell'avatar di un utente",
    async: true,
    return: 'Promise<SafeUrl>',
  })
  getAvatarUrl(userId?: number): Promise<string> {
    if (!userId)
      return Promise.resolve('../../../assets/skeleton/empty-avatar.png');
    return this.siaUsersService.getUserAvatar(userId).then((url: any) => {
      if (!url) {
        url = '../../../assets/skeleton/empty-avatar.png';
      }
      return url;
    });
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'pathHasContent(archivePath: any): Promise<boolean>',
    comment:
      "Verifica ricorsivamente se un percorso contiene file all'interno di un archivio",
    async: true,
    return: 'Promise<boolean>',
  })
  async pathHasContent(archiveId: string, path: string): Promise<boolean> {
    const archivePath: SiaArchivePath = {
      id: archiveId,
      path,
    };
    return await this.fileService.pathHasContent(archivePath);
  }
  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'isModuleEnabled(moduleId: string): Promise<boolean>',
    comment:
      "Verifica ricorsivamente se un percorso contiene file all'interno di un archivio",
    async: true,
    return: 'Promise<boolean>',
  })
  async isModuleEnabled(moduleId: string): Promise<boolean> {
    return await this.componentConfigService.isModuleEnabled(moduleId);
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'focusFieldById(fieldId: string)',
    comment: 'Sposta il focus su un elemento HTML tramite il suo id',
    async: false,
    return: 'void',
  })
  focusFieldById(fieldId: string) {
    const searchField = document.getElementById(fieldId);
    searchField?.focus();
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition: 'reproduceSound(soundName: string, loop?: boolean)',
    comment: "Interrompe l'audio in corso e riproduce il nuovo file",
    async: false,
    return: 'void',
  })
  reproduceSound(soundName: string, loop: boolean = false) {
    this.#audio.pause();
    this.#audio.currentTime = 0;
    this.#audio.src = `../assets/shared/sounds/${soundName}`;
    this.#audio.loop = loop;
    this.#audio.play().catch(() => {});
  }

  @MethodInfo({
    className: 'SiaBaseScriptComponent',
    methodDefinition:
      'generateGoogleCalendarLink(title: string, startDate: Date, endDate: Date, description?: string, location?: string): string',
    comment: 'Genera un link per creare un evento su Google Calendar',
    async: false,
    return: 'string',
  })
  generateGoogleCalendarLink(
    title: string,
    startDate: Date,
    endDate: Date,
    description: string = '',
    location: string = '',
  ): string {
    const event: GoogleCalendarEvent = {
      title,
      description,
      location,
      startTime: startDate,
      endTime: endDate,
    };

    const url = generateGoogleCalendarLink(event);

    return url;
  }

  navigateToHomepage() {
    let isMobile = this.siaSizeDetector.currentSize.isMobile;
    let userHomeMenu = isMobile
      ? this.siaUsersService.getUserData()?.redirectMasterMenuId
      : this.siaUsersService.getUserData()?.redirectMobileMenuId;
    if (!userHomeMenu) {
      this.notify('Nessun menu di default configurato', 2000, 'info');
      this.siaRouterService.navigateMenuWithParams('default-login');
      return;
    }
    this.siaRouterService.navigateMenuWithParams(
      'close^' + (userHomeMenu ?? ''),
    );
  }
}
